// src/App.jsx
import React, { Suspense, lazy } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import JsonLd       from './components/JsonLd';
import { siteSchema } from './lib/seoSchemas';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load route components for code splitting
const Login        = lazy(() => import('./components/Login'));
const AuthedLayout = lazy(() => import('./components/AuthedLayout'));
const Logout       = lazy(() => import('./components/Logout'));
const Callback     = lazy(() => import('./components/Callback'));

// Loading fallback component
export const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2em',
    color: 'var(--color-primary-text, #333)'
  }}>
    <h1>Apex Vision AI Chat</h1>
    <p>Loading...</p>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      {/* ─── Site‑wide defaults ─── */}
      <Helmet
        titleTemplate="%s – Apex AI Chat"
        defaultTitle="Apex AI Chat – AI Tutoring for Homework, Math, Flashcards & More"
      >
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#5E99BD" />

        <meta property="og:site_name" content="Apex AI Chat" />
        <meta property="og:type"      content="website" />
        <meta
          property="og:image"
          content="https://chat.apexvision.ai/logo512.png"
        />
      </Helmet>

      {/* ─── Site‑wide JSON‑LD (Organization + WebSite) ─── */}
      <JsonLd data={siteSchema} />

      {/* ─── Routing ─── */}
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login"         element={<Login />} />
            <Route path="/auth-callback" element={<Callback />} />
            <Route path="/chat"          element={<AuthedLayout />} />
            <Route path="/logout"        element={<Logout />} />
            <Route path="/"              element={<AuthedLayout />} />
            <Route path="*"              element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
